/**
 * Fleet dashboard severity classification (operational mission control).
 * Authority: Fleet Dashboard Spec — Critical / Watch / Offline / Stable.
 * Numeric severity_score (0–100), tiers by score, reserve awareness, why generation.
 */

import type { FleetSystem } from '@/data/fleet-types';
import { POLL_STALE_MINUTES, POLL_OFFLINE_MINUTES } from '@/lib/poll-status';

export type FleetSeverity = 'critical' | 'watch' | 'offline' | 'stable';

/** Offline threshold (minutes) — canonical source: poll-status.ts */
const OFFLINE_THRESHOLD_MIN = POLL_OFFLINE_MINUTES;
/** Staleness threshold (minutes) — canonical source: poll-status.ts */
const STALENESS_THRESHOLD_MIN = POLL_STALE_MINUTES;

export interface FleetSystemWithSeverity extends FleetSystem {
  severity: FleetSeverity;
  /** Minutes since last poll (null if never polled) */
  offlineMinutes: number | null;
  /** Numeric score 0–100 (higher = worse). Used for tier and sort. */
  severityScore: number;
}

export interface FleetSummaryCounts {
  critical: number;
  watch: number;
  offline: number;
  curtailment: number;
  underperforming: number;
  stable: number;
  total: number;
}

/**
 * offline_weight: 1 if offline > 30 min, else 0.
 */
function offlineWeight(online: boolean, offlineMinutes: number | null): number {
  if (online) return 0;
  if (offlineMinutes == null) return 1;
  return offlineMinutes > OFFLINE_THRESHOLD_MIN ? 1 : 0;
}

/**
 * staleness_weight: 1 if last_poll > 15 min, else 0 (use dataStale as proxy).
 */
function stalenessWeight(dataStale: boolean): number {
  return dataStale ? 1 : 0;
}

/**
 * Severity score formula (clamped 0–100):
 * 60×breach_probability + 20×offline_weight + 10×staleness_weight + 5×pv_underperformance_score + 5×curtailment_score
 */
function computeSeverityScore(s: FleetSystem, offlineMin: number | null): number {
  const offW = offlineWeight(s.online, s.online ? null : offlineMin);
  const stW = stalenessWeight(s.dataStale);
  const raw =
    60 * (s.breach_probability ?? 0) +
    20 * offW +
    10 * stW +
    5 * (s.pv_underperformance_score ?? 0) +
    5 * (s.curtailment_score ?? 0);
  return Math.max(0, Math.min(100, Math.round(raw)));
}

/**
 * Map severity_score to tier. Offline systems remain Critical regardless of score.
 * >= 60 → Critical; 30–59 → Watch; < 30 → Stable.
 */
function scoreToTier(score: number, online: boolean): FleetSeverity {
  if (!online) return 'offline';
  if (score >= 60) return 'critical';
  if (score >= 30) return 'watch';
  return 'stable';
}

/**
 * Format projected_breach_time for "why" (e.g. "3:40 AM").
 */
function formatBreachTime(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true });
  } catch {
    return iso;
  }
}

/**
 * Generate why.primary per system. Priority:
 * 1. Offline → "Offline for X minutes."
 * 2. breach_probability > 0.6 → "Projected reserve breach at TIME."
 * 3. pv_underperformance_score > 0.5 → "PV X% below expected clear-sky output."
 * 4. curtailment_score > 0.5 → "Battery full; curtailing X kWh today." or "Battery full; X kWh wasted today."
 * 5. Else → "System operating within expected range."
 */
function formatOfflineDuration(minutes: number): string {
  if (minutes >= 24 * 60) {
    const days = Math.floor(minutes / (24 * 60));
    return days === 1 ? '1 day' : `${days} days`;
  }
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  return minutes === 1 ? '1 minute' : `${minutes} minutes`;
}

function computeWhyPrimary(s: FleetSystem, offlineMinutes: number | null): string {
  if (!s.online) {
    if (offlineMinutes != null) {
      return `Offline for ${formatOfflineDuration(offlineMinutes)}.`;
    }
    // No last-good-data timestamp: we've never received meaningful telemetry from this system.
    return 'Never connected. No meaningful data received.';
  }
  if ((s.breach_probability ?? 0) > 0.6 && s.projected_breach_time) {
    const time = formatBreachTime(s.projected_breach_time);
    return time ? `Projected reserve breach at ${time}.` : 'Projected reserve breach before sunrise.';
  }
  if ((s.breach_probability ?? 0) > 0.6) {
    return 'Projected reserve breach before sunrise.';
  }
  // WT-341 v3: prefer the honest pv_state — a telemetry failure must not read as "PV below expected".
  if (s.pvState === 'dead_pv_telemetry') {
    return 'Solar telemetry not reporting — cannot read PV production.';
  }
  if (s.pvState === 'partial_inverter_outage') {
    return 'One or more inverters not reporting solar production.';
  }
  if (s.pvState === 'underperforming') {
    return s.underperformance_pct != null
      ? `PV ${Math.round(s.underperformance_pct)}% below expected output.`
      : 'PV below expected output.';
  }
  // Legacy path (pv_state not yet computed): the old score-based signal.
  if (s.pvState == null && (s.pv_underperformance_score ?? 0) > 0.5 && s.underperformance_pct != null) {
    return `PV ${Math.round(s.underperformance_pct)}% below expected clear-sky output.`;
  }
  if ((s.curtailment_score ?? 0) > 0.5) {
    const wasted = s.wasted_kwh_today ?? 0;
    return wasted > 0
      ? `Battery full; curtailing ${wasted.toFixed(1)} kWh today.`
      : 'Battery full; curtailing today.';
  }
  return s.why?.primary ?? 'System operating within expected range.';
}

/**
 * Timestamp of last good data: last non-empty telemetry, or last successful poll.
 * Do not use lastPollAt when last poll failed or was success_empty — that would show
 * "Offline for 10 min" when the system has had no good data for days.
 */
function lastGoodDataAt(s: FleetSystem): string | null {
  if (s.lastTelemetryAt != null) return s.lastTelemetryAt;
  if (s.lastPollStatus === 'success' && s.lastPollAt != null) return s.lastPollAt;
  return null;
}

/**
 * Minutes since last good data. When offline, this is the true "offline for X" duration.
 */
function minutesSinceLastGoodData(s: FleetSystem): number | null {
  const ts = lastGoodDataAt(s);
  if (ts == null) return null;
  return Math.floor((Date.now() - new Date(ts).getTime()) / (60 * 1000));
}

export function addSeverityToSystems(systems: FleetSystem[]): FleetSystemWithSeverity[] {
  const now = Date.now();
  return systems.map((s) => {
    const minutesSinceGood = minutesSinceLastGoodData(s);
    const offlineMinutes = !s.online ? minutesSinceGood : null;

    let severityScore = computeSeverityScore(s, offlineMinutes);
    // Only flag below-reserve if we actually have telemetry data (otherwise 0 kWh is a default, not real)
    const belowReserveNow =
      s.hasTelemetry !== false &&
      (s.current_soc_kwh ?? 0) < (s.reserve_kwh ?? 0) && (s.reserve_kwh ?? 0) > 0;
    if (belowReserveNow) {
      severityScore = 100;
    }

    const severity = belowReserveNow ? 'critical' : scoreToTier(severityScore, s.online);
    let finalSeverity: FleetSeverity = severity === 'offline' ? 'offline' : severity;
    // Grid-tied: reserve/forecast risk and current below-reserve are Watch, not Critical (FLEET_DASHBOARD_SPEC).
    if (finalSeverity === 'critical' && s.gridMode === 'grid_primary') {
      finalSeverity = 'watch';
    }
    // WT-341 v3 (WT-345/362): an online PV attention state (dead/partial telemetry or real underperformance)
    // must not render in the green "stable" tier — floor it at Watch. The legacy pv_underperformance_score
    // path is inert (dormant RPC), so without this the honest state would show green on the fleet dashboard.
    if (finalSeverity === 'stable'
        && (s.pvState === 'dead_pv_telemetry' || s.pvState === 'partial_inverter_outage' || s.pvState === 'underperforming')) {
      finalSeverity = 'watch';
    }
    const finalScore = finalSeverity === 'offline' ? 100 : severityScore;

    const whyPrimary = computeWhyPrimary(s, offlineMinutes);

    const withSev: FleetSystemWithSeverity = {
      ...s,
      severity: finalSeverity,
      offlineMinutes: s.online ? null : offlineMinutes,
      severityScore: finalScore,
      why: { ...s.why, primary: whyPrimary },
    };
    return withSev;
  });
}

export function getFleetSummaryCounts(systems: FleetSystemWithSeverity[]): FleetSummaryCounts {
  let critical = 0;
  let watch = 0;
  let offline = 0;
  let curtailment = 0;
  let underperforming = 0;
  let stable = 0;
  for (const s of systems) {
    if (s.severity === 'critical') critical++;
    else if (s.severity === 'watch') watch++;
    else if (s.severity === 'offline') offline++;
    else stable++;
    if (s.is_curtailing) curtailment++;
    if (s.is_underperforming) underperforming++;
  }
  return {
    critical,
    watch,
    offline,
    curtailment,
    underperforming,
    stable,
    total: systems.length,
  };
}

/**
 * Urgency sort: projected_breach_time asc, then severity_score desc, then SOC asc.
 * No breach_time → fall back to severity_score desc, then SOC asc.
 */
export function sortBySeverity(
  systems: FleetSystemWithSeverity[],
  sortKey: 'severity' | 'name' | 'soc'
): FleetSystemWithSeverity[] {
  const copy = [...systems];
  if (sortKey === 'name') {
    copy.sort((a, b) => (a.systemName ?? '').localeCompare(b.systemName ?? ''));
    return copy;
  }
  if (sortKey === 'soc') {
    copy.sort((a, b) => a.socPct - b.socPct);
    return copy;
  }
  copy.sort((a, b) => {
    const aBreach = a.projected_breach_time ? new Date(a.projected_breach_time).getTime() : Infinity;
    const bBreach = b.projected_breach_time ? new Date(b.projected_breach_time).getTime() : Infinity;
    if (aBreach !== bBreach) return aBreach - bBreach;
    if (b.severityScore !== a.severityScore) return b.severityScore - a.severityScore;
    return a.socPct - b.socPct;
  });
  return copy;
}

/**
 * Within Critical section: SOC below reserve now first, then breach in < 2h, then severity_score desc, SOC asc.
 */
const BREACH_NEAR_MS = 2 * 60 * 60 * 1000;

export function sortCriticalByUrgency(
  systems: FleetSystemWithSeverity[]
): FleetSystemWithSeverity[] {
  const copy = [...systems];
  const now = Date.now();
  copy.sort((a, b) => {
    const aBelowReserve = (a.current_soc_kwh ?? 0) < (a.reserve_kwh ?? 0) ? 1 : 0;
    const bBelowReserve = (b.current_soc_kwh ?? 0) < (b.reserve_kwh ?? 0) ? 1 : 0;
    if (bBelowReserve !== aBelowReserve) return aBelowReserve - bBelowReserve;

    const aBreachMs = a.projected_breach_time ? new Date(a.projected_breach_time).getTime() - now : Infinity;
    const bBreachMs = b.projected_breach_time ? new Date(b.projected_breach_time).getTime() - now : Infinity;
    const aBreachSoon = aBreachMs > 0 && aBreachMs <= BREACH_NEAR_MS ? 1 : 0;
    const bBreachSoon = bBreachMs > 0 && bBreachMs <= BREACH_NEAR_MS ? 1 : 0;
    if (bBreachSoon !== aBreachSoon) return aBreachSoon - bBreachSoon;

    if (b.severityScore !== a.severityScore) return b.severityScore - a.severityScore;
    return a.socPct - b.socPct;
  });
  return copy;
}

/** Section filter: which severity bucket to show (or 'all') */
export type FleetSectionFilter = 'all' | FleetSeverity;

export function filterBySection(
  systems: FleetSystemWithSeverity[],
  section: FleetSectionFilter
): FleetSystemWithSeverity[] {
  if (section === 'all') return systems;
  return systems.filter((s) => s.severity === section);
}
